import { invoiceToExchangedValue } from "$routes/_lib/billing-v2/currency-exchange";
import { useCurrencyExchangeFormat } from "$routes/_lib/use-currency-exchange-format";
import { useFormatPenceFactory } from "$routes/_lib/use-format-pence";
import { useMemo } from "react";
import { array, number, object, string } from "yup";
import { TRANSACTION_TOTAL } from "../components/formConstants";
import {
  type ExchangeFor,
  NO_EXCHANGE,
  rowExchange,
  totalInvoiceValueOf,
} from "../components/payment-rows";

const paymentTypeOptions = [
  { label: "Cash", value: "CASH" },
  { label: "Waiver", value: "WAIVER" },
];

export const useTransactionsSchema = ({
  totalPayableInPence,
  minValue = 1,
  exchangeFor = NO_EXCHANGE,
}: {
  totalPayableInPence: number;
  minValue?: number;
  exchangeFor?: ExchangeFor;
}) => {
  const formatPence = useFormatPenceFactory();
  const { formatMoney } = useCurrencyExchangeFormat();

  return useMemo(
    () =>
      object({
        transactions: array()
          .of(
            object({
              amountInPence: number().min(minValue).required(),
              type: string(),
              method: string()
                .oneOf(paymentTypeOptions.map(({ value }) => value))
                .required(),
            }),
          )
          .min(1)
          .test(
            TRANSACTION_TOTAL,
            `Total amount must not exceed ${formatPence(totalPayableInPence)}`,
            function testTotal(transactions = []) {
              const waiveAmount =
                transactions.find(({ type }) => type === "WAIVER")
                  ?.amountInPence ?? 0;
              const transactionTotal = totalInvoiceValueOf(
                transactions,
                totalPayableInPence,
                exchangeFor,
              );

              const isValid =
                transactionTotal > 0 && transactionTotal <= totalPayableInPence;

              if (isValid) {
                return true;
              }

              if (transactionTotal <= 0) {
                return this.createError({
                  path: TRANSACTION_TOTAL,
                  message: `Total amount must be greater than ${formatPence(
                    0,
                  )}`,
                });
              }

              const payableInPence = totalPayableInPence - waiveAmount;
              const exchange = transactions
                .map((row) => rowExchange(row, exchangeFor))
                .find(Boolean);
              return this.createError({
                path: TRANSACTION_TOTAL,
                message: `Total amount must not exceed ${formatPence(
                  payableInPence,
                )}${
                  exchange
                    ? ` (${formatMoney(
                        exchange.currency,
                        invoiceToExchangedValue(
                          Math.max(0, payableInPence),
                          exchange.currency,
                          exchange.rate,
                        ),
                      )})`
                    : ""
                }`,
              });
            },
          ),
      }),
    [totalPayableInPence, minValue, formatPence, formatMoney, exchangeFor],
  );
};
